import { Box, Grid, Spinner } from "@chakra-ui/react";
import CDS from "design-system";
import { useCallback, useEffect, useMemo, useRef } from "react";
import moment from "moment-timezone";
import TimeColumn from "components/Calendar/primitives/TimeColumn";
import CalendarColumn from "components/Calendar/primitives/CalendarColumn";
import { PositionedEvent } from "components/Calendar/types";
import { calculatePositionedEvents } from "components/Calendar/primitives/calendarLogic";
import { MeetingId } from "features/Resources/Meeting/models";
import useAvailabilityQuery from "features/Resources/Meeting/hooks/useAvailabilityQuery";
import ColumnHeader from "./ColumnHeader";
import SelectionSummary from "./SelectionSummary";
import Legend from "./Legend";
import { busyPeriodsToCalendarEvents, buildGetSlotStyle } from "./helpers";
import CrossColumnOverlay from "./CrossColumnOverlay";
import LocalTimeLabels from "./LocalTimeLabels";
import { Participant } from "./types";

const SLOT_HEIGHT = 40;
const TIME_COL_WIDTH = 60;
const HOURS = Array.from({ length: 24 }, (_, i) => i);

interface FindATimeViewProps {
  meetingId: MeetingId;
  date: Date | undefined;
  timezone: string;
  additionalMeetingIds?: string[];
  selectedTime: Date | undefined;
  onSelectTime: (time: Date) => void;
  reservationStartTime?: string;
}

function buildPositionedEvents(participant: Participant, date: Date, timezone: string): PositionedEvent[] {
  const events = busyPeriodsToCalendarEvents(participant);
  if (events.length === 0) return [];

  return calculatePositionedEvents({
    events,
    startDate: date,
    numDays: 1,
    slotHeight: SLOT_HEIGHT,
    timezone,
  });
}

export default function FindATimeView({
  meetingId,
  date,
  timezone,
  additionalMeetingIds,
  selectedTime,
  onSelectTime,
  reservationStartTime,
}: FindATimeViewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dateStr = date ? moment(date).format("YYYY-MM-DD") : "";

  const { data, isLoading, isError } = useAvailabilityQuery(
    meetingId,
    {
      date: dateStr,
      timezone,
      additionalMeetingIds,
    },
    { enabled: !!date },
  );

  const participants = useMemo(() => data?.participants ?? [], [data?.participants]);
  const suggestedTimes = useMemo(() => {
    const times = data?.suggestedTimes ?? [];
    if (times.length === 0) return [];
    const best = times.reduce((a, b) => (b.score > a.score ? b : a));
    return [best];
  }, [data?.suggestedTimes]);
  const durationMinutes = data?.meeting?.durationMinutes ?? 30;

  const currentMeetingPeriod = useMemo(() => {
    for (const p of participants) {
      const bp = p.busyPeriods.find((b) => b.source === "current_meeting");
      if (bp) return bp;
    }
    // Fall back to the tentative reservation time (e.g., from bulk scheduling),
    // but only if the reservation falls on the currently viewed day
    if (reservationStartTime && date) {
      const reservationDate = moment.tz(reservationStartTime, timezone).format("YYYY-MM-DD");
      const viewedDate = moment.tz(date, timezone).format("YYYY-MM-DD");
      if (reservationDate === viewedDate) {
        return {
          startTime: reservationStartTime,
          endTime: new Date(new Date(reservationStartTime).getTime() + durationMinutes * 60 * 1000).toISOString(),
          label: "Current reservation",
          source: "current_meeting" as const,
        };
      }
    }
    return undefined;
  }, [participants, reservationStartTime, durationMinutes, date, timezone]);

  // Pre-select highest scored suggested time on first load
  useEffect(() => {
    if (suggestedTimes.length > 0 && !selectedTime) {
      const best = suggestedTimes.reduce((a, b) => (b.score > a.score ? b : a));
      onSelectTime(new Date(best.startTime));
    }
  }, [suggestedTimes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to first suggested time or 9am on load
  useEffect(() => {
    if (!scrollRef.current || !data) return;
    let scrollTo: number;
    if (suggestedTimes.length > 0) {
      const earliest = suggestedTimes.reduce((a, b) => (new Date(a.startTime) < new Date(b.startTime) ? a : b));
      const m = moment.tz(earliest.startTime, timezone);
      scrollTo = Math.max(0, (m.hours() - 1) * SLOT_HEIGHT);
    } else {
      scrollTo = 8 * SLOT_HEIGHT; // 8am
    }
    scrollRef.current.scrollTop = scrollTo;
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const getSlotStyle = useMemo(() => buildGetSlotStyle(participants, timezone), [participants, timezone]);

  const participantEvents = useMemo(() => {
    if (!date) return [];
    return participants.map((p) => buildPositionedEvents(p, date, timezone));
  }, [participants, date, timezone]);

  const handleGridClick = useCallback(
    (e: React.MouseEvent) => {
      const container = scrollRef.current;
      if (!container || participants.length === 0) return;

      const rect = container.getBoundingClientRect();
      const offsetX = e.clientX - rect.left + container.scrollLeft;
      const offsetY = e.clientY - rect.top + container.scrollTop;

      // Ignore clicks on the time column
      if (offsetX < TIME_COL_WIDTH) return;

      // Snap to 15-minute intervals
      const totalMinutes = Math.floor((offsetY / SLOT_HEIGHT) * 60);
      const snappedMinutes = Math.floor(totalMinutes / 15) * 15;

      const clickTime = moment.tz(date, timezone).startOf("day").add(snappedMinutes, "minutes").toDate();

      onSelectTime(clickTime);
    },
    [date, timezone, participants.length, onSelectTime],
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <Spinner size="md" mr={3} />
        <CDS.Text12 color="gray.500">
          Loading availability...
        </CDS.Text12>
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box py={4}>
        <CDS.Text12 color="red.500">
          Unable to load availability. Please try again.
        </CDS.Text12>
      </Box>
    );
  }

  if (participants.length === 0) {
    return (
      <Box py={4}>
        <CDS.Text12 color="gray.500">
          No participant availability to display.
        </CDS.Text12>
      </Box>
    );
  }

  const gridColumns = `${TIME_COL_WIDTH}px repeat(${participants.length}, 1fr)`;
  const noopMouse = () => {};

  return (
    <Box>
      {/* Column headers */}
      <Grid templateColumns={gridColumns}>
        <Box display="flex" alignItems="center" justifyContent="center" height={`${SLOT_HEIGHT}px`}>
          <CDS.Text10 fontSize="2xs" color="gray.500" fontWeight="medium">
            {moment.tz(timezone).format("z")}
          </CDS.Text10>
        </Box>
        {participants.map((p, i) => (
          <ColumnHeader key={p.id ?? i} participant={p} slotHeight={SLOT_HEIGHT} />
        ))}
      </Grid>

      {/* Scrollable calendar body */}
      <Box
        ref={scrollRef}
        position="relative"
        overflowY="auto"
        maxHeight="min(260px, calc(100vh - 600px))"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        cursor="pointer"
        onClick={handleGridClick}
      >
        <Grid templateColumns={gridColumns} height={`${24 * SLOT_HEIGHT}px`} position="relative">
          <TimeColumn timeColumnWidth={TIME_COL_WIDTH} slotHeight={SLOT_HEIGHT} hours={HOURS} />

          {participants.map((p, i) => (
            <CalendarColumn
              key={p.id ?? i}
              columnIndex={i}
              date={date!}
              events={participantEvents[i] ?? []}
              hours={HOURS}
              slotHeight={SLOT_HEIGHT}
              show15Minutes={false}
              timezone={p.timezone || timezone}
              dragged={null}
              resizing={null}
              onMouseDown={noopMouse}
              onResizeStart={noopMouse}
              getSlotStyle={getSlotStyle}
            >
              <LocalTimeLabels
                columnTimezone={p.timezone || timezone}
                schedulingTimezone={timezone}
                date={date!}
                slotHeight={SLOT_HEIGHT}
                hours={HOURS}
              />
            </CalendarColumn>
          ))}

          {/* Single cross-column overlay using the scheduling timezone */}
          <Box
            position="absolute"
            top={0}
            left={`${TIME_COL_WIDTH}px`}
            right={0}
            bottom={0}
            pointerEvents="none"
            zIndex={10}
          >
            <CrossColumnOverlay
              selectedTime={selectedTime}
              suggestedTimes={suggestedTimes}
              currentMeeting={currentMeetingPeriod}
              durationMinutes={durationMinutes}
              slotHeight={SLOT_HEIGHT}
              timezone={timezone}
            />
          </Box>
        </Grid>
      </Box>

      <SelectionSummary
        selectedTime={selectedTime}
        durationMinutes={durationMinutes}
        timezone={timezone}
        participants={participants}
      />
      <Legend />
    </Box>
  );
}
